/**
 * SFX latency debugger.
 * Records timestamps at each step of the audio pipeline
 * and exposes them for visual overlay rendering.
 */
import * as Tone from "tone"

interface SfxTimingEvent {
  id: number
  key: string
  // Pipeline timestamps (performance.now() ms)
  inputDown: number // pointerdown event fired
  playSfxEnter: number // playSfx() function entered
  contextCheck: number // after ensureContext()
  dispatchEnter: number // ToneSfxSynthesizer.play() entered
  triggerCall: number // triggerAttackRelease called
  triggerDone: number // triggerAttackRelease returned
  // Derived
  totalMs: number
}

let _enabled = false
let _eventId = 0
let _current: Partial<SfxTimingEvent> | null = null
const _history: SfxTimingEvent[] = []
const MAX_HISTORY = 12

export const SfxDebug = {
  get enabled() {
    return _enabled
  },

  enable() {
    _enabled = true
  },
  disable() {
    _enabled = false
  },

  /** Step 1: call immediately before playSfx in the same handler */
  markInputDown(key: string) {
    if (!_enabled) return
    _current = { id: ++_eventId, key, inputDown: performance.now() }
  },

  /** Step 2: entering playSfx() */
  markPlaySfxEnter(key?: string) {
    if (!_enabled) return
    // If no current event from markInputDown, create one (non-instrumented caller)
    if (!_current) {
      _current = {
        id: ++_eventId,
        key: key ?? "?",
        inputDown: performance.now(),
      }
    }
    if (key) _current.key = key
    _current.playSfxEnter = performance.now()
  },

  /** Step 3: after context check */
  markContextCheck() {
    if (!_enabled || !_current) return
    _current.contextCheck = performance.now()
  },

  /** Step 4: entering dispatcher */
  markDispatchEnter() {
    if (!_enabled || !_current) return
    _current.dispatchEnter = performance.now()
  },

  /** Step 5: about to call triggerAttackRelease */
  markTriggerCall() {
    if (!_enabled || !_current) return
    _current.triggerCall = performance.now()
  },

  /** Step 6: triggerAttackRelease returned */
  markTriggerDone() {
    if (!_enabled || !_current) return
    _current.triggerDone = performance.now()
    _current.totalMs =
      _current.triggerDone - (_current.inputDown ?? _current.triggerDone)

    const event = _current as SfxTimingEvent
    _history.push(event)
    if (_history.length > MAX_HISTORY) _history.shift()

    _current = null
  },

  /** Get recent events for overlay rendering */
  getHistory(): readonly SfxTimingEvent[] {
    return _history
  },

  /** Get the latest event */
  getLatest(): SfxTimingEvent | null {
    return _history.length > 0 ? _history[_history.length - 1] : null
  },

  /** Get WebAudio hardware latency info */
  getAudioLatencyInfo(): {
    baseLatency: number
    outputLatency: number
    sampleRate: number
    state: string
  } {
    const ctx = Tone.getContext().rawContext as AudioContext
    return {
      baseLatency: (ctx.baseLatency ?? 0) * 1000, // convert s → ms
      outputLatency: (ctx.outputLatency ?? 0) * 1000, // convert s → ms
      sampleRate: ctx.sampleRate,
      state: ctx.state,
    }
  },
}
